/* eslint-disable jsx-a11y/label-has-for, jsx-a11y/label-has-associated-control */
import React, {
  useCallback, useState, useEffect, useContext
} from 'react';
import PropTypes from 'prop-types';
import { Select, LabelText } from '@glu/form-components';
import { FormContext } from '@glu/validation-react';
import { withStyles } from '@glu/theming';
import locale from '@glu/locale';
import classnames from 'classnames';
import useDebounce from '../../utility/useDebounce';
import Color from '../Color/Color';
import styles from './ColorWithGradient.styles';

const linearGradientRegex = /\(([0-9]*)deg,\s?([#A-Za-z0-9]+) ([0-9]+)%,\s?([#A-Za-z0-9]+) ([0-9]+)%\)$/;

const getInitialColors = (value) => {
  const isGradient = value.includes('linear-gradient');
  if (isGradient) {
    const [, angle, color1, percentage1, color2, percentage2] = value.match(linearGradientRegex);
    return {
      isGradient,
      angle: parseInt(angle, 10),
      color1,
      percentage1: parseInt(percentage1, 10),
      color2,
      percentage2: parseInt(percentage2, 10)
    };
  }
  return {
    isGradient, angle: 180, color1: value, percentage1: 0, color2: value, percentage2: 100
  };
};

const ColorWithGradient = ({
  classes, name, labelText, backgroundColor, fontSize, fontWeight
}) => {
  const { data, onChange } = useContext(FormContext);
  const value = data[name] || '';
  const [colorType, setColorType] = useState('solid');
  const [angle, setAngle] = useState(0);
  const [color1, setColor1] = useState(value);
  const [percentage1, setPercentage1] = useState(0);
  const [color2, setColor2] = useState(value);
  const [percentage2, setPercentage2] = useState(100);
  const [colorActive, setColorActive] = useState(undefined);
  const debouncedAngle = useDebounce(angle, 500);
  const debouncedPercentage1 = useDebounce(percentage1, 500);
  const debouncedPercentage2 = useDebounce(percentage2, 500);

  const onChangeColor1 = useCallback((_name, newValue) => {
    setColor1(newValue);
  }, []);

  const onChangeColor2 = useCallback((_name, newValue) => {
    setColor2(newValue);
  }, []);

  const onChangeColorType = useCallback(({ target: { value: newValue } }) => {
    setColorType(newValue);
  }, []);

  const onChangeAngle = useCallback(({ target: { value: newValue } }) => {
    const newValueNumber = parseInt(newValue, 10);
    if (/[^0-9]/.test(newValue)) {
      return;
    }
    if (newValueNumber > 360) {
      return;
    }
    setAngle(!newValue ? '' : newValueNumber);
  }, []);

  const onChangePercentage1 = useCallback(({ target: { value: newValue } }) => {
    const newValueNumber = parseInt(newValue, 10);
    if (newValueNumber > percentage2) {
      return;
    }
    setPercentage1(newValueNumber);
  }, [percentage2]);

  const onChangePercentage2 = useCallback(({ target: { value: newValue } }) => {
    const newValueNumber = parseInt(newValue, 10);
    if (newValueNumber < percentage1) {
      return;
    }
    setPercentage2(newValueNumber);
  }, [percentage1]);

  const onClickColor1 = useCallback(() => {
    setColorActive('color1');
  }, []);

  const onClickColor2 = useCallback(() => {
    setColorActive('color2');
  }, []);

  useEffect(() => {
    const {
      isGradient,
      angle: initialAngle,
      color1: initialColor1,
      percentage1: initialPercentage1,
      color2: initialColor2,
      percentage2: initialPercentage2
    } = getInitialColors(value);
    setColorType(isGradient ? 'linearGradient' : 'solid');
    setAngle(initialAngle);
    setColor1(initialColor1);
    setPercentage1(initialPercentage1);
    setColor2(initialColor2);
    setPercentage2(initialPercentage2);
  }, [value]);

  useEffect(() => {
    setColorActive(undefined);
  }, [angle, colorType]);

  useEffect(() => {
    if (colorType === 'linearGradient') {
      onChange(
        name,
        `linear-gradient(${debouncedAngle || 0}deg, ${color1} ${debouncedPercentage1}%, ${color2} ${debouncedPercentage2}%)`
      );
    } else {
      onChange(name, color1);
    }
  }, [
    color1,
    color2,
    colorType,
    debouncedAngle,
    debouncedPercentage1,
    debouncedPercentage2,
    name,
    onChange
  ]);

  const firstColorZIndex = colorActive === 'color1' ? 10 : undefined;
  const percentageLeftAngle = `${angle / 360 * 100}%`;

  return (
    <div className={`${classes.root} colorWithGradient`}>
      <Select
        name="colorType"
        labelText={labelText}
        value={colorType}
        options={[
          { value: 'solid', label: locale.get('branding.solid') },
          { value: 'linearGradient', label: locale.get('branding.linearGradient') }
        ]}
        disableInputAPI
        onChange={onChangeColorType}
      />
      {colorType === 'solid'
        ? (
          <Color
            name={name}
            labelText={locale.get('branding.solidColor')}
            backgroundColor={backgroundColor}
            fontSize={fontSize}
            fontWeight={fontWeight}
            screenReaderLabel
            className={classes.input}
          />
        ) : (
          <>
            <div
              className={classes.gradientInput}
              style={{
                background: `linear-gradient(to right, ${color1} ${percentage1}%, ${color2} ${percentage2}%)`
              }}
            >
              <label htmlFor={`${name}percentage1`}>
                <LabelText label={locale.get('branding.color1Percentage')} screenReaderLabel />
              </label>
              <input
                type="range"
                id={`${name}percentage1`}
                name="percentage1"
                min={0}
                max={100}
                step={1}
                value={percentage1}
                onChange={onChangePercentage1}
                onMouseDown={onClickColor1}
                style={{ zIndex: firstColorZIndex ? firstColorZIndex + 1 : undefined }}
              />
              <label htmlFor={`${name}percentage2`}>
                <LabelText label={locale.get('branding.color2Percentage')} screenReaderLabel />
              </label>
              <input
                type="range"
                id={`${name}percentage2`}
                name="percentage2"
                min={0}
                max={100}
                step={1}
                value={percentage2}
                onMouseDown={onClickColor2}
                onChange={onChangePercentage2}
              />
              <div
                className={classnames(classes.gradientHandle, { active: colorActive === 'color1' })}
                style={{ left: `${percentage1}%`, zIndex: firstColorZIndex }}
              >
                <div className={classes.arrow} />
                <div className={classes.colorPickerWrap}>
                  <div className={classes.colorIndicator} style={{ background: color1 }} />
                </div>
              </div>
              <div
                className={classnames(classes.gradientHandle, { active: colorActive === 'color2' })}
                style={{ left: `${percentage2}%` }}
              >
                <div className={classes.arrow} />
                <div className={classes.colorPickerWrap}>
                  <div className={classes.colorIndicator} style={{ background: color2 }} />
                </div>
              </div>
            </div>
            <Color
              name="color1"
              labelText={locale.get('branding.color1')}
              backgroundColor={backgroundColor}
              fontSize={fontSize}
              fontWeight={fontWeight}
              value={color1}
              onChange={onChangeColor1}
              onClick={onClickColor1}
              className={classnames(classes.colorInput, { active: colorActive === 'color1' })}
            />
            <Color
              name="color2"
              labelText={locale.get('branding.color2')}
              backgroundColor={backgroundColor}
              fontSize={fontSize}
              fontWeight={fontWeight}
              value={color2}
              onChange={onChangeColor2}
              onClick={onClickColor2}
              className={classnames(classes.colorInput, { active: colorActive === 'color2' })}
            />
            <div className={classes.angleWrap}>
              <label htmlFor={`${name}rotation`}>
                <LabelText htmlFor={`${name}rotation`} label={locale.get('branding.rotation')} />
                <input
                  className={classes.angleSlider}
                  type="range"
                  name="angle"
                  id={`${name}rotation`}
                  value={angle || 0}
                  min={0}
                  max={360}
                  step={1}
                  onChange={onChangeAngle}
                  style={{
                    background: `linear-gradient(to right, #4c4c4c 0%, #4c4c4c ${percentageLeftAngle}, #ccc ${percentageLeftAngle}, #ccc 100%)`
                  }}
                />
              </label>
              <label htmlFor={`${name}rotationText`}>
                <LabelText label={locale.get('branding.rotationText')} screenReaderLabel />
              </label>
              <div
                className={classes.angleHandle}
                style={{
                  left: percentageLeftAngle,
                  marginLeft: -13 - parseInt(percentageLeftAngle, 10) / 100 * 7
                }}
              >
                <input id={`${name}rotationText`} type="text" value={angle} onChange={onChangeAngle} min={0} max={360} />
                <span>&#176;</span>
              </div>
            </div>
          </>
        )}
    </div>
  );
};

ColorWithGradient.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  name: PropTypes.string.isRequired,
  labelText: PropTypes.string.isRequired,
  backgroundColor: PropTypes.string,
  fontSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fontWeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

ColorWithGradient.defaultProps = {
  backgroundColor: null,
  fontSize: null,
  fontWeight: null
};

export default withStyles(styles)(ColorWithGradient);
